import { action } from '@reatom/framework'
import BigNumber from 'bignumber.js'
import i18next from 'i18next'
import { nanoid } from 'nanoid'

import { Array, Result } from '@blade/std-lib'
import {
  reatomAggregatedPosition,
  type AggregatedPositionModel,
  type WalletPositionModel,
} from '@root/entities/position'
import {
  processBulkSwapAction,
  processSellSwapAction,
} from '@root/features/trade/swap'
import { tradeSettingsAtom } from '@root/features/trade/trade-settings'
import { UnknownError } from '@root/shared'
import { reatomRequest } from '@root/shared/lib'
import { showSnackbarAction } from '@root/shared/ui-kit'

import { SellPositionSnackbar } from '../../sell-position-snackbar'

import { SellEntirePositionModel } from './multiple-swap-requests-model'

export const processDumpPositionsAction = action(
  async (ctx, position: AggregatedPositionModel) => {
    const swapSettings = ctx.get(tradeSettingsAtom)
    const walletPositions = ctx.get(position.walletPositions)
    const pair = position.pair
    const accounts = ctx.get(pair.accounts)
    const pairQuoteTokenProgram = ctx.get(pair.quoteTokenProgram)!

    const requestModel = new SellEntirePositionModel(position)

    showSnackbarAction(ctx, {
      delay: 'permanent',
      element: <SellPositionSnackbar request={requestModel} />,
    })

    const result = await ctx.schedule(() =>
      processBulkSwapAction(ctx, {
        pairAddress: pair.address,
        pairQuoteCurrency: pair.quote.address,
        pairQuoteTokenProgram,
        swapSettings,
        type: 'sell',
        accounts,
        network: position.network,
        inAddress: pair.quote.address,
        outAddress: pair.base.address,
        id: nanoid(),
        provider: pair.provider,
        swaps: walletPositions.map((it) => {
          const wallet = ctx.get(it.walletAtom)
          const walletAsset = wallet
            ? ctx.get(wallet.assetsByCurrency)[pair.quote.address]
            : undefined
          const rawAmountIn = walletAsset
            ? (ctx.get(walletAsset.rawBalance) ?? undefined)
            : undefined
          return {
            walletId: it.walletId,
            walletAddress: it.walletAddress,
            amountIn: ctx.get(it.remainingSizeCurrency),
            amountOut: BigNumber(0),
            rawAmountIn,
          }
        }),
      }),
    )

    if (Result.isFailure(result)) {
      requestModel.handleError(ctx, result.failure)
    } else {
      requestModel.handleSignAndSendSwapData(ctx, result.success)
    }

    return result
  },
  'processDumpPositionsAction',
)

const dumpSingleWalletPositionAction = action(
  async (ctx, walletPosition: WalletPositionModel) => {
    const wallet = ctx.get(walletPosition.walletAtom)

    if (!wallet) {
      return Result.fail(UnknownError.fromUnknown('Wallet not found'))
    }
    const swapSettings = ctx.get(tradeSettingsAtom)

    return processSellSwapAction(ctx, {
      amount: 'full',
      pair: walletPosition.pair,
      wallet: wallet,
      settings: swapSettings,
      quote: undefined,
    })
  },
  'dumpSingleWalletPositionAction',
)

export const reatomDumpPosition = ({
  position,
}: {
  position: AggregatedPositionModel | WalletPositionModel
}) => {
  const processAction = action(async (ctx) => {
    const singlePosition = (() => {
      if (reatomAggregatedPosition.is(position)) {
        const positionWallets = ctx.get(position.walletPositions)
        return Array.single(positionWallets)
      }
      return position
    })()

    if (!singlePosition) {
      if (reatomAggregatedPosition.is(position)) {
        const result = await ctx.schedule(() =>
          processDumpPositionsAction(ctx, position),
        )
        return result.pipe(Result.map((_it) => undefined))
      }
      return Result.fail(UnknownError.fromUnknown('No position found'))
    }

    const result = await ctx.schedule(() =>
      dumpSingleWalletPositionAction(ctx, singlePosition),
    )

    if (Result.isFailure(result)) {
      showSnackbarAction(ctx, {
        text: i18next.t('common.something-went-wrong'),
        intent: 'error',
      })
    } else {
      showSnackbarAction(ctx, {
        text: i18next.t('position.position-closed-all-message', {
          ticker: position.pair.quote.ticker,
        }),
        intent: 'success',
      })
    }

    return result.pipe(Result.map((_it) => undefined))
  }, 'processAction')

  const { executeAction: dumpPositionsAction, requestStateAtom: closeState } =
    reatomRequest({
      execAction: processAction,
      name: 'reatomSellAllPositions',
    })

  return {
    executeSellAction: dumpPositionsAction,
    requestState: closeState,
  }
}
